import { Plus, XCircle } from 'lucide-react';
import { CostItem, CostReference } from '../types';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import BaseSpreadsheet, { ColumnConfig } from './BaseSpreadsheet';

const ItemTypes = { COST_ITEM: 'costItem' };

interface CostSpreadsheetProps {
  costs: CostItem[];
  canEdit: boolean;
  onCostChange: (index: number, field: keyof CostItem, value: any) => void;
  onRemoveCost: (id: string) => void;
  onMoveCost: (fromIndex: number, toIndex: number) => void;
  onAddCost: () => void;
  onSave: () => void;
  totalBudget: number;
}

const COST_COLUMNS: ColumnConfig[] = [
  { id: 'id', label: 'ID', visible: true, width: 'col-span-2' },
  { id: 'code', label: 'Code', visible: true, width: 'col-span-1' },
  { id: 'description', label: 'Description', visible: true, width: 'col-span-3' },
  { id: 'category', label: 'Category', visible: true, width: 'col-span-2' },
  { id: 'amount', label: 'Amount', visible: true, width: 'col-span-2' },
];

export default function CostSpreadsheet({
  costs,
  canEdit,
  onCostChange,
  onRemoveCost,
  onMoveCost,
  onAddCost,
  onSave,
  totalBudget,
}: CostSpreadsheetProps) {
  const renderCostCell = (
    cost: CostItem,
    columnId: string,
    canEdit: boolean,
    onCellChange: (field: string, value: any) => void
  ) => {
    if (canEdit) {
      switch (columnId) {
        case 'id':
          return (
            <input
              type="text"
              value={cost.id}
              onChange={(e) => onCellChange('id', e.target.value.toUpperCase())}
              className={`w-full text-xs ${APP_NATIVE_FIELD_CLASS}`}
              placeholder="ID"
            />
          );
        case 'code':
          return (
            <input
              type="text"
              value={cost.code || ''}
              onChange={(e) => onCellChange('code', e.target.value.toUpperCase())}
              className={`w-full text-xs ${APP_NATIVE_FIELD_CLASS}`}
              placeholder="Code"
            />
          );
        case 'description':
          return (
            <input
              type="text"
              value={cost.description}
              onChange={(e) => onCellChange('description', e.target.value)}
              className={`w-full text-xs ${APP_NATIVE_FIELD_CLASS}`}
              placeholder="Description"
            />
          );
        case 'category':
          return (
            <input
              type="text"
              value={cost.category}
              onChange={(e) => onCellChange('category', e.target.value)}
              className={`w-full text-xs ${APP_NATIVE_FIELD_CLASS}`}
              placeholder="Category"
            />
          );
        case 'amount':
          return (
            <input
              type="number"
              min="0"
              step="0.01"
              value={cost.amount}
              onChange={(e) => onCellChange('amount', Number(e.target.value))}
              className={`w-full text-xs ${APP_NATIVE_FIELD_CLASS}`}
              placeholder="Amount"
            />
          );
        default:
          return null;
      }
    } else {
      switch (columnId) {
        case 'amount':
          return (
            <div className="text-xs text-foreground truncate">
              ${Number(cost.amount || 0).toLocaleString()}
            </div>
          );
        case 'id':
          return <div className="text-xs text-foreground truncate">{cost.id}</div>;
        case 'code':
          return <div className="text-xs text-foreground truncate">{cost.code || '—'}</div>;
        case 'description':
          return <div className="text-xs text-foreground truncate">{cost.description}</div>;
        case 'category':
          return <div className="text-xs text-foreground truncate">{cost.category}</div>;
        default:
          return null;
      }
    }
  };

  const renderReferencesRow = (
    cost: CostItem,
    canEdit: boolean,
    onCellChange: (field: string, value: any) => void
  ) => {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">References ({(cost.references || []).length})</p>
          {canEdit && (
            <button
              type="button"
              onClick={() =>
                onCellChange('references', [
                  ...(cost.references || []),
                  {
                    type: 'material',
                    name: '',
                    url: '',
                    cost: 0,
                    unit: '',
                    source: '',
                  },
                ])
              }
              className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-accent"
              title="Add reference"
            >
              <Plus className="w-3 h-3" />
              Add Ref
            </button>
          )}
        </div>

        {(cost.references || []).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No references yet</p>
        ) : (
          <div className="space-y-2">
            {(cost.references || []).map((ref, refIndex) => (
              <div key={refIndex} className="flex gap-1 items-end text-xs">
                {canEdit ? (
                  <>
                    <select
                      value={ref.type}
                      onChange={(e) => {
                        const next = [...(cost.references || [])];
                        next[refIndex] = { ...next[refIndex], type: e.target.value as CostReference['type'] };
                        onCellChange('references', next);
                      }}
                      className={`${APP_NATIVE_FIELD_CLASS} w-20`}
                      title="Reference type"
                    >
                      <option value="product">Product</option>
                      <option value="labor">Labor</option>
                      <option value="material">Material</option>
                      <option value="service">Service</option>
                    </select>
                    <input
                      type="text"
                      value={ref.name}
                      onChange={(e) => {
                        const next = [...(cost.references || [])];
                        next[refIndex] = { ...next[refIndex], name: e.target.value };
                        onCellChange('references', next);
                      }}
                      className={`${APP_NATIVE_FIELD_CLASS} w-24`}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={ref.source || ''}
                      onChange={(e) => {
                        const next = [...(cost.references || [])];
                        next[refIndex] = { ...next[refIndex], source: e.target.value };
                        onCellChange('references', next);
                      }}
                      className={`${APP_NATIVE_FIELD_CLASS} w-20`}
                      placeholder="Source"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ref.cost}
                      onChange={(e) => {
                        const next = [...(cost.references || [])];
                        next[refIndex] = { ...next[refIndex], cost: Number(e.target.value) };
                        onCellChange('references', next);
                      }}
                      className={`${APP_NATIVE_FIELD_CLASS} w-16`}
                      placeholder="Cost"
                    />
                    <input
                      type="text"
                      value={ref.unit || ''}
                      onChange={(e) => {
                        const next = [...(cost.references || [])];
                        next[refIndex] = { ...next[refIndex], unit: e.target.value };
                        onCellChange('references', next);
                      }}
                      className={`${APP_NATIVE_FIELD_CLASS} w-12`}
                      placeholder="Unit"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = [...(cost.references || [])];
                        next.splice(refIndex, 1);
                        onCellChange('references', next);
                      }}
                      className="px-1 py-1 text-destructive hover:bg-destructive/10 rounded flex-shrink-0 ml-auto"
                      title="Remove reference"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-medium flex-shrink-0">{ref.type}</span>
                    <span className="truncate max-w-[60px]">{ref.name}</span>
                    <span className="truncate max-w-[50px]">{ref.source}</span>
                    <span className="flex-shrink-0">${Number(ref.cost || 0).toLocaleString()}</span>
                    <span className="text-muted-foreground flex-shrink-0">{ref.unit}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <BaseSpreadsheet<CostItem>
      data={costs}
      columns={COST_COLUMNS}
      canEdit={canEdit}
      onDataChange={onCostChange}
      onRemove={onRemoveCost}
      onMove={onMoveCost}
      onAdd={onAddCost}
      onSave={onSave}
      renderCell={renderCostCell}
      renderExpandedRow={renderReferencesRow}
      getRowId={(cost) => cost.id}
      totalLabel="Total Budget"
      totalValue={totalBudget}
      emptyMessage="No cost items yet"
      ItemTypes={ItemTypes}
      dragDropType="costItem"
    />
  );
}
